import React, { useCallback, useEffect, useState } from 'react';
import { Bell, X, CalendarCheck, Megaphone, Wallet, Loader2, Settings, ShieldCheck } from 'lucide-react';
import { useToast } from './Toast';
import { getAlertSettingsDB } from '../../services/db';
import { useAuth } from '../../context/AuthContext';

const DISMISS_KEY = 'asmm_push_prompt_dismissed_at';
const REASK_INTERVAL_MS = 12 * 60 * 60 * 1000; // 12 jam
const INITIAL_DELAY_MS = 4000;

const isPushSupported = () =>
  typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator;

const isSubscribed = () => isPushSupported() && Notification.permission === 'granted';

const getLastDismissed = (): number => {
  try {
    return Number(localStorage.getItem(DISMISS_KEY)) || 0;
  } catch {
    return 0;
  }
};

const urlBase64ToUint8Array = (base64String: string) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from(Array.from(raw, (c) => c.charCodeAt(0)));
};

/**
 * Best-effort Web Push subscription via PushManager (only when a valid VAPID key is configured).
 * Permission grant alone is treated as "subscribed" for prompt purposes.
 */
const trySubscribePushManager = async () => {
  try {
    if (!('PushManager' in window)) return;
    let reg = await navigator.serviceWorker.getRegistration();
    if (!reg) reg = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    if (existing) return;
    const settings = await getAlertSettingsDB();
    const key = settings?.vapidPublicKey || '';
    if (!/^[A-Za-z0-9_-]{80,}$/.test(key)) return; // placeholder / invalid key → skip
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key)
    });
    localStorage.setItem('asmm_push_subscription', JSON.stringify(sub));
  } catch (err) {
    console.warn('PushManager subscription skipped:', err);
  }
};

export const PushSubscribePrompt: React.FC = () => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>(
    isPushSupported() ? Notification.permission : 'default'
  );

  const shouldPrompt = useCallback(() => {
    if (!currentUser) return false;
    if (!isPushSupported() || isSubscribed()) return false;
    return Date.now() - getLastDismissed() >= REASK_INTERVAL_MS;
  }, [currentUser]);

  // Close if user logs out
  useEffect(() => {
    if (!currentUser && isOpen) {
      setIsOpen(false);
      setIsVisible(false);
    }
  }, [currentUser, isOpen]);

  // Initial check + periodic re-check (covers long-lived tabs / installed PWA)
  useEffect(() => {
    if (!currentUser || !isPushSupported()) return;
    const check = () => {
      if (shouldPrompt()) {
        setPermission(Notification.permission);
        setIsOpen(true);
      }
    };
    const initial = window.setTimeout(check, INITIAL_DELAY_MS);
    const interval = window.setInterval(check, 15 * 60 * 1000);
    const onVisible = () => document.visibilityState === 'visible' && check();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearTimeout(initial);
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [currentUser, shouldPrompt]);

  // Lock body scroll + enter animation
  useEffect(() => {
    if (!isOpen) return;
    document.body.classList.add('modal-open');
    document.body.style.overflow = 'hidden';
    const t = window.setTimeout(() => setIsVisible(true), 15);
    return () => {
      window.clearTimeout(t);
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const close = (remember = true) => {
    if (remember) {
      try {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      } catch { /* ignore */ }
    }
    setIsVisible(false);
    window.setTimeout(() => setIsOpen(false), 200);
  };

  const handleSubscribe = async () => {
    setIsRequesting(true);
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result === 'granted') {
        await trySubscribePushManager();
        showToast('success', 'Notifikasi Diaktifkan', 'Anda akan menerima makluman latihan, notis & yuran terus ke peranti ini.');
        close(false);
      } else if (result === 'denied') {
        showToast('warning', 'Notifikasi Disekat', 'Sila benarkan notifikasi melalui tetapan pelayar untuk melanggan.');
      } else {
        close();
      }
    } catch {
      showToast('error', 'Ralat', 'Tidak dapat mengaktifkan notifikasi pada peranti ini.');
    } finally {
      setIsRequesting(false);
    }
  };

  if (!isOpen || !currentUser) return null;

  const isDenied = permission === 'denied';
  const benefits = [
    {
      icon: CalendarCheck,
      title: 'Peringatan Sesi Latihan',
      description: 'Pemberitahuan jadual mingguan & persiapan ujian',
      gradient: 'from-red-500 via-red-600 to-rose-700',
      shadow: 'shadow-red-500/25'
    },
    {
      icon: Megaphone,
      title: 'Notis & Pengumuman',
      description: 'Pekeliling guru utama & warta terkini pertubuhan',
      gradient: 'from-blue-600 to-blue-800',
      shadow: 'shadow-blue-600/25'
    },
    {
      icon: Wallet,
      title: 'Log Yuran & Invois',
      description: 'Peringatan status & rekod pembayaran gelanggang',
      gradient: 'from-amber-400 via-amber-500 to-amber-600',
      shadow: 'shadow-amber-500/25'
    }
  ];

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4 no-print"
      role="dialog"
      aria-modal="true"
      aria-labelledby="push-prompt-title"
    >
      {/* Luxury Cinematic Backdrop */}
      <div
        className={`absolute inset-0 bg-slate-950/70 backdrop-blur-md transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'
          }`}
        onClick={() => close()}
      />

      <div
        className={`relative w-full max-w-md bg-white rounded-[2rem] border border-slate-100 shadow-[0_25px_70px_-15px_rgba(15,23,42,0.25)] p-6 sm:p-8 pt-7 sm:pt-8 transition-all duration-300 ease-out ${isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-95'
          }`}
      >
        {/* Subtle Ambient Top Radial Glow */}
        <div className="absolute inset-x-0 top-0 h-36 bg-gradient-to-b from-red-50/70 via-amber-50/25 to-transparent rounded-t-[2rem] pointer-events-none" />

        {/* Close Button */}
        <button
          id="push-prompt-close"
          type="button"
          onClick={() => close()}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-100/90 hover:bg-slate-200 text-slate-400 hover:text-slate-700 flex items-center justify-center transition-all cursor-pointer backdrop-blur-xs z-20"
          aria-label="Tutup"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Title & Eyebrow Header */}
        <div className="text-center relative z-10 space-y-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50/90 border border-red-200/80 text-asmm-red text-[11px] font-black uppercase tracking-wider shadow-2xs">
            <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse" />
            <span>Saluran Makluman Rasmi</span>
          </div>

          <h2 id="push-prompt-title" className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight leading-snug">
            {isDenied ? 'Notifikasi Sedang Disekat' : 'Jangan Terlepas Makluman!'}
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 leading-relaxed max-w-xs mx-auto font-medium">
            {isDenied
              ? 'Benarkan notifikasi untuk portal ini melalui tetapan kebenaran pelayar anda.'
              : 'Aktifkan notifikasi peranti untuk terima makluman latihan, notis rasmi & invois tepat pada masanya.'}
          </p>
        </div>

        {/* Denied Warning Card */}
        {isDenied ? (
          <div className="mt-5 p-4 rounded-2xl bg-amber-50/70 border border-amber-200/80 text-left space-y-2 relative z-10">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0 shadow-xs">
                <Settings className="w-4 h-4" />
              </div>
              <div className="text-xs font-bold text-amber-950">
                Cara Menyahsekat Notifikasi
              </div>
            </div>
            <p className="text-xs text-amber-900/90 leading-relaxed pl-10.5 font-medium">
              Tekan ikon 🔒 pada bar alamat pelayar anda → Pilih <b>Kebenaran Laman (Permissions)</b> → Tukar <b>Notifikasi</b> kepada <b>Benarkan (Allow)</b>, kemudian muat semula halaman.
            </p>
          </div>
        ) : (
          /* Executive Feature Cards */
          <div className="mt-5 sm:mt-6 space-y-2.5 relative z-10">
            {benefits.map((item) => (
              <div
                key={item.title}
                className="group flex items-center gap-3.5 p-3 sm:p-3.5 rounded-2xl bg-slate-50/80 hover:bg-slate-50 border border-slate-100/90 hover:border-slate-200 transition-all shadow-2xs"
              >
                <div
                  className={`w-10 h-10 rounded-xl bg-gradient-to-br ${item.gradient} text-white flex items-center justify-center shrink-0 shadow-xs ${item.shadow} group-hover:scale-105 transition-transform`}
                >
                  <item.icon className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-xs sm:text-sm font-bold text-slate-900 leading-tight">
                    {item.title}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate mt-0.5 font-medium">
                    {item.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Action Buttons & Trust Guarantee */}
        <div className="mt-6 sm:mt-7 space-y-2.5 relative z-10">
          {!isDenied && (
            <button
              id="push-prompt-subscribe"
              type="button"
              onClick={handleSubscribe}
              disabled={isRequesting}
              className="w-full relative group overflow-hidden py-3.5 sm:py-4 px-6 rounded-full bg-gradient-to-r from-red-600 via-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 text-white text-xs sm:text-sm font-black shadow-lg shadow-red-600/25 hover:shadow-xl hover:shadow-red-600/35 transition-all transform active:scale-[0.99] flex items-center justify-center gap-2.5 cursor-pointer disabled:opacity-70 border-none"
            >
              {/* Shimmer sweep effect */}
              <div className="absolute inset-0 w-1/2 h-full bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 -translate-x-full group-hover:translate-x-[300%] transition-transform duration-1000 ease-out pointer-events-none" />
              {isRequesting ? (
                <Loader2 className="w-4 h-4 animate-spin text-white" />
              ) : (
                <Bell className="w-4 h-4 text-amber-300 fill-amber-300/30" />
              )}
              <span>Aktifkan Notifikasi</span>
            </button>
          )}

          <button
            id="push-prompt-later"
            type="button"
            onClick={() => close()}
            className="w-full py-2.5 rounded-full text-xs font-bold text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition cursor-pointer"
          >
            {isDenied ? 'Faham & Tutup' : 'Nanti Dahulu'}
          </button>

          {/* Micro Trust Guarantee Footer */}
          <div className="pt-2 border-t border-slate-100 flex items-center justify-center gap-1.5 text-[11px] font-medium text-slate-400">
            <ShieldCheck className="w-3.5 h-3.5 text-slate-400" />
            <span>Notifikasi Rasmi PSSASMM · Boleh dinyahaktifkan bila-bila masa</span>
          </div>
        </div>
      </div>
    </div>
  );
};

